import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { CatalogItem, ReleaseKitSummary } from '../../../../../redux/api/fymAPI.types';
import { daysToRelease } from '../studioRules';
import { nextUpAssetTypes } from './nextUpAssetTypes';
import { songPickerMessages as m } from './songPickerMessages';
import { SongPickerHero } from './SongPickerHero';
import { SongPickerFacts } from './SongPickerFacts';
import { SongPickerMade } from './SongPickerMade';
import { SongPickerNextUp } from './SongPickerNextUp';
import { Panel } from '../../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './SongPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

const NO_PALETTE: string[] = [];

export interface SongPickerCardProps {
  item: CatalogItem;
  /** The kit summary of the song (`GET /release-kits`); undefined when the song has none. */
  summary: ReleaseKitSummary | undefined;
  now: Date;
  /** "Make something" and the hero: no asset type. A Next up chip: its asset type. */
  onPick: (catalogItemUuid: string, assetTypeId?: StudioThemeId) => void;
  /** "Add the master": opens Song files of the song page in Catalog. */
  onAddMaster: (item: CatalogItem) => void;
  /** "Song page": opens the song page of the release. */
  onOpenSongPage: (catalogItemUuid: string) => void;
}

/**
 * One song in the Studio song picker (design t1): the hero in the song's release theme, the facts,
 * what is made, "Next up" and the actions. A song with a missing master gets "Add the master" as the
 * primary action; its hero still picks the song.
 */
const SongPickerCardComponent: React.FC<SongPickerCardProps> = ({
  item,
  summary,
  now,
  onPick,
  onAddMaster,
  onOpenSongPage,
}) => {
  const intl = useIntl();
  const days = useMemo(() => daysToRelease(item.releaseDate, now), [item.releaseDate, now]);
  const nextUp = useMemo(() => nextUpAssetTypes(item, now), [item, now]);
  const { uuid } = item;
  const pick = useCallback(() => onPick(uuid), [onPick, uuid]);
  const pickType = useCallback((assetTypeId: StudioThemeId) => onPick(uuid, assetTypeId), [onPick, uuid]);
  const addMaster = useCallback(() => onAddMaster(item), [item, onAddMaster]);
  const openSongPage = useCallback(() => onOpenSongPage(uuid), [onOpenSongPage, uuid]);
  const needsMaster = !item.song.hasMaster || item.song.masterStatus === null || item.song.masterStatus === 'failed';

  return (
    <Panel as="article" className={styles.card} data-testid={`song-picker-card-${uuid}`}>
      <SongPickerHero
        item={item}
        look={summary?.look ?? null}
        palette={summary?.palette ?? NO_PALETTE}
        days={days}
        onClick={pick}
      />
      <div className={styles.body}>
        <SongPickerFacts item={item} days={days} />
        <SongPickerMade summary={summary} />
        <SongPickerNextUp nextUp={nextUp} onPick={pickType} />
        <div className={styles.actions}>
          {needsMaster ? (
            <SMLButton omitCap size="sm" onClick={addMaster}>
              {intl.formatMessage(m.addMaster)}
            </SMLButton>
          ) : (
            <SMLButton omitCap size="sm" onClick={pick}>
              {intl.formatMessage(m.makeSomething)}
            </SMLButton>
          )}
          <SMLButton outline omitCap size="sm" onClick={openSongPage}>
            {intl.formatMessage(m.songPage)}
          </SMLButton>
        </div>
      </div>
    </Panel>
  );
};

export const SongPickerCard = TypedMemo(SongPickerCardComponent);
